// PageHeader (DESIGN §3.5.1): meldet Titel und Zurück-Ziel an die Topbar und rendert die
// Kopfzeile der Seite (links Tabs/Filter, rechts Aktionen, Abstand 12). Varianten `profile`
// (Kopf-Karte mit Avatar) und `entity` (Icon-Kachel statt Avatar).
import { ArrowLeft } from '@phosphor-icons/react';
import { Fragment, type ReactNode } from 'react';
import { Card } from './Card';
import { cn } from './cn';
import { RouterLink } from './Link';
import { type BackTarget, usePageTitle } from './page-chrome';

export interface PageHeaderProps {
  /** Seitentitel (Topbar-h1 bzw. im Inhalt, wenn die Shell keinen Topbar-Titel hat). */
  title: string;
  back?: BackTarget;
  /** Satz unter dem Titel (nur wenn der Titel im Inhalt steht, z. B. Bar AI). */
  description?: ReactNode;
  /** Links in der Kopfzeile: PillTabs oder Filter. */
  tabs?: ReactNode;
  /** Rechts: höchstens eine Primär-Pill, dann Sekundär-Pills, zuletzt „…“. */
  actions?: ReactNode;
  variant?: 'section' | 'profile' | 'entity';
  /** profile: Avatar-Element (52); entity: Icon-Kachel. */
  avatar?: ReactNode;
  /** Name im Kopf (Standard: `title`). */
  name?: ReactNode;
  /** StatusBadge neben dem Namen. */
  badge?: ReactNode;
  /** Meta-Zeile (je Eintrag Icon 14 + Text), mit „·“ getrennt. */
  meta?: readonly ReactNode[];
  className?: string;
  /** Titel immer im Inhalt rendern (mobile Admin-Detailseiten, Bar-AI-Unterseiten). */
  titleInContent?: boolean;
  appName?: string;
}

export function BackButton({ back, className }: { back: BackTarget; className?: string }) {
  return (
    <RouterLink
      href={back.href}
      aria-label={back.label}
      className={cn(
        'inline-flex size-10 shrink-0 items-center justify-center rounded-full text-fg transition-colors duration-(--bar-dur-fast) hover:bg-pill-page',
        className,
      )}
    >
      <ArrowLeft size={20} aria-hidden />
    </RouterLink>
  );
}

function MetaLine({ meta }: { meta: readonly ReactNode[] }) {
  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-meta text-fg-secondary">
      {meta.map((entry, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <span aria-hidden className="text-fg-decor">
              ·
            </span>
          ) : null}
          <span className="inline-flex min-w-0 items-center gap-1 [&_svg]:size-3.5 [&_svg]:shrink-0">
            {entry}
          </span>
        </Fragment>
      ))}
    </p>
  );
}

export function PageHeader({
  title,
  back,
  description,
  tabs,
  actions,
  variant = 'section',
  avatar,
  name,
  badge,
  meta,
  className,
  titleInContent,
  appName,
}: PageHeaderProps) {
  const renderTitle = usePageTitle(title, back, appName) || Boolean(titleInContent);
  const row =
    tabs || actions ? (
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">{tabs}</div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    ) : null;

  if (variant === 'section') {
    return (
      <div data-slot="page-header" className={cn('mb-4 flex min-w-0 flex-col gap-3', className)}>
        {renderTitle ? (
          <div className="flex min-w-0 items-center gap-2">
            {back ? <BackButton back={back} className="-ml-2" /> : null}
            <div className="min-w-0">
              <h1 className="truncate text-title-page-m text-fg md:text-title-page">{title}</h1>
              {description ? (
                <p className="mt-1 text-body text-fg-secondary">{description}</p>
              ) : null}
            </div>
          </div>
        ) : null}
        {row}
      </div>
    );
  }

  const NameTag = renderTitle ? 'h1' : 'h2';
  return (
    <div
      data-slot="page-header"
      data-variant={variant}
      className={cn('mb-4 flex min-w-0 flex-col gap-3', className)}
    >
      <Card className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:gap-4">
        {avatar ? <div className="shrink-0">{avatar}</div> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <NameTag className="min-w-0 truncate text-title-profile text-fg">
              {name ?? title}
            </NameTag>
            {badge}
          </div>
          {meta && meta.length > 0 ? <MetaLine meta={meta} /> : null}
          {/* Aktionen unter der Meta-Zeile (perf_s2_3, DESIGN §3.5.1). */}
          {actions ? <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      </Card>
      {tabs ? <div className="min-w-0">{tabs}</div> : null}
    </div>
  );
}
